import React from 'react';
import { useCountdownTimer } from '../../hooks/useCountdownTimer';
import { getCountryFlag, formatOdds } from '../../utils/common';
import { buildSelectedOutcomeData } from '../../utils/ticketHelpers';
import { renderLotteryLogo } from '../../utils/renderLotteryLogo';
import classes from './LotteryCard.module.scss';

const isUrgentCountdown = (value) => {
  if (/^\d+d/.test(value)) return false;
  const hourMatch = value.match(/^(\d+)h/);
  if (!hourMatch) return true;
  return parseInt(hourMatch[1], 10) < 6;
};

const LotteryCardComponent = ({ lottery, onSelect, onOutcomeSelect }) => {
  const suggestedCategory = lottery.suggested_markets?.[0];
  const suggestedMarkets = suggestedCategory?.markets ?? [];
  const nextDraw = lottery.active_draw_events?.[0];

  const { countdown, isExpired: isBetsClosed } = useCountdownTimer({
    targetDate: nextDraw?.draw_date,
    status: nextDraw?.status,
  });

  const countdownColor = countdown && isUrgentCountdown(countdown) ? '#e27338' : '#2c6b2f';

  const handleClick = () => {
    if (isBetsClosed) return;
    onSelect({
      lotteryId: lottery.lottery_id,
      country: lottery.country,
      shortName: lottery.short_name,
      shouldShowCategories: suggestedMarkets.length > 0,
    });
  };

  const handleSuggestedClick = (event, market, outcome) => {
    event.stopPropagation();
    if (isBetsClosed || !suggestedCategory) return;

    const lotteryMetadata = {
      lottery_id: lottery.lottery_id,
      lottery_name: `${lottery.country} - ${lottery.short_name}`,
      main_grid_total: lottery.main_grid_total,
      secondary_grid_total: lottery.secondary_grid_total,
      secondary_unique: lottery.secondary_unique,
      next_draw: nextDraw,
    };

    onOutcomeSelect(buildSelectedOutcomeData(
      lotteryMetadata,
      suggestedCategory.key,
      suggestedCategory.title,
      market.market_type,
      market.title,
      market.main_grid_numbers,
      market.secondary_grid_numbers,
      outcome
    ));
  };

  return (
    <div
      role="button"
      tabIndex={isBetsClosed ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick();
      }}
      className={classes.Card + (isBetsClosed ? ' ' + classes.Closed : '')}
    >
      <div className={classes.Header}>
        <span className={classes.CountryBadge}>
          <span>{getCountryFlag(lottery.country)}</span>
          <span>{lottery.country}</span>
        </span>
      </div>

      <div className={classes.LotteryInfo}>
        <div className={classes.LogoContainer}>{renderLotteryLogo(lottery.lottery_id)}</div>
        <div className={classes.LotteryInfoText}>
          <h4 className={classes.LotteryName}>{lottery.short_name}</h4>
          {countdown && (
            <div className={classes.CountdownRow}>
              <span className={classes.CountdownDot} style={{ backgroundColor: countdownColor }} />
              <span className={classes.Countdown} style={{ color: countdownColor }}>{countdown}</span>
              <span className={classes.UntilLabel}>until draw</span>
            </div>
          )}
        </div>
      </div>

      {suggestedMarkets.length > 0 && (
        <div className={classes.Content}>
          <p className={classes.MarketLabel}>{suggestedCategory?.title || 'Numbers'}</p>
          <div className={classes.SuggestedRow}>
            {suggestedMarkets.map((market) => {
              const firstOutcome = market.outcomes?.[0];
              return (
                <button
                  key={market.market_type}
                  type="button"
                  onClick={(event) => handleSuggestedClick(event, market, firstOutcome)}
                  className={classes.SuggestedButton}
                >
                  <span className={classes.SuggestedTitle}>{market.title}</span>
                  <span className={classes.SuggestedOdds}>{formatOdds(firstOutcome?.odds)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isBetsClosed && (
        <div className={classes.ClosedOverlay}>
          <span className={classes.ClosedWatermark}>Closed</span>
        </div>
      )}
    </div>
  );
};

const LotteryCard = React.memo(LotteryCardComponent);

export default LotteryCard;
